import { Stack, xcss, Box } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { addFlag } from '@databases/flags';
import usePanel from '@databases/panel/usePanel';
import { getTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { ViewId } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getDatabaseFields } from '@databases/utils/database/utils';
import {
  createView,
  getViewById,
  ViewChangeType,
  ViewChangeTypes,
  viewHasChanges,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { isEqualOmitUndefined } from '@databases/utils/utils';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import useDeleteView from '../../hooks/useDeleteView';
import useView from '../../hooks/useView';
import { useViewUiState } from '../../hooks/useViewUiState';
import { messages } from '../../i18n';
import { ViewPanelSettingType } from '../../types/types';
import { handleSaveView } from '../../utils/utils';
import { getUniqueViewName, isUniqueViewName } from '../../utils/viewName';
import ViewSettingsFields from '../viewSettingsFields/ViewSettingsFields';
import ViewSettingsFilter from '../viewSettingsFilter/ViewSettingsFilter';
import ViewSettingsFooter from '../viewSettingsFooter/ViewSettingsFooter';
import ViewSettingsHeader from '../viewSettingsHeader/ViewSettingsHeader';
import ViewSettingsLayout from '../viewSettingsLayout/ViewSettingsLayout';
import ViewSettingsOverview from '../viewSettingsOverview/ViewSettingsOverview';
import ViewSettingsSort from '../viewSettingsSort/ViewSettingsSort';

const stackStyles = xcss({
  width: '100%',
  height: '100%',
});

const bodyBoxStyles = xcss({
  padding: 'space.300',
});

export interface ViewSettingsProps {
  FieldPicker: React.FunctionComponent<any>;
  FilterTypePicker: React.FunctionComponent<any>;
  FilterValueEdit: React.FunctionComponent<any>;
}

const ViewSettings: FunctionComponent<ViewSettingsProps> = (props) => {
  const { contentId } = useDatabaseContent();

  if (!contentId) {
    return null;
  }

  return <ViewSettingsInternal {...props} contentId={contentId} />;
};

const ViewSettingsInternal: FunctionComponent<
  ViewSettingsProps & { contentId: string }
> = ({ FieldPicker, FilterTypePicker, FilterValueEdit, contentId }) => {
  const [viewNameIsValid, setViewNameIsValid] = useState<boolean>(true);
  const { view, getViewChangeList, setViewId, unsavedView } = useView();
  const { activePanelSetting, setActivePanelSetting } = useViewUiState();
  const { hidePanel, getData, setUnsavedChanges, isPanelVisible } = usePanel();

  const [viewName, setViewName] = useState(view.name);

  const { autoFocusLastFilterValue, canEditViews } = getData<'ViewSettingsPanel'>();
  const handleDeleteView = useDeleteView(contentId);

  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const [totalViews, viewNames, databaseFields, savedViews] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const filteredViews = database.views.filter((v) => v.id !== view.id);
      return [
        database.views.length,
        filteredViews.map((view) => view.name.trim().toLowerCase()),
        getDatabaseFields(database),
        database.views,
      ];
    });

  const originalView = useMemo(
    () => getViewById(savedViews, view.id),
    [savedViews, view.id]
  );

  const hasChanges = useMemo(() => {
    if (unsavedView && view.id === ViewId.UNSAVED) {
      return true;
    }
    return (
      (originalView &&
        (viewHasChanges(view.settings, originalView.settings) ||
          isUniqueViewName(viewName, [...viewNames, originalView.name]))) ??
      false
    );
  }, [originalView, unsavedView, view.id, view.settings, viewName, viewNames]);

  useEffect(() => {
    setUnsavedChanges('ViewSettingsPanel', hasChanges);
  }, [hasChanges, setUnsavedChanges]);

  useEffect(() => {
    setViewNameIsValid(
      viewName.trim() !== '' && !viewNames.includes(viewName.trim().toLowerCase())
    );
  }, [originalView, viewNameIsValid, viewNames, viewName]);

  useEffect(() => {
    if (!isPanelVisible('ViewSettingsPanel')) {
      setActivePanelSetting(null);
    }
  }, [isPanelVisible, setActivePanelSetting]);

  const onClose = useCallback(() => {
    hidePanel({ ignoreUnsavedChanges: !canEditViews });
  }, [canEditViews, hidePanel]);

  const handleSaveAsNewView = useCallback(() => {
    const newView = createView(view.settings);
    const isRenamed = viewName !== originalView?.name;

    const getViewName = () => {
      if (isRenamed) {
        return viewName;
      }

      if (
        originalView &&
        isUniqueViewName(view.name, [...viewNames, originalView.name])
      ) {
        return view.name;
      } else {
        return getUniqueViewName(
          {
            ...newView,
            name: unsavedView ? '' : view.name,
          },
          [...savedViews.map((v) => v.name), view.name],
          intl
        );
      }
    };

    const newViewName = getViewName();

    dispatch(
      addChangeToDraft(contentId, 'main', {
        type: DatabaseChangeType.ADD_SAVED_VIEW,
        view: {
          ...newView,
          name: newViewName,
        },
      })
    );

    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);

    client.sendDatabaseCreateViewEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      viewId: newView.id,
      source: 'databaseViewSettingsPanel',
      layoutType: newView.settings.layout.type,
    });

    setViewId(newView.id);
  }, [
    client,
    contentId,
    dispatch,
    intl,
    originalView,
    savedViews,
    setActivePanelSetting,
    setViewId,
    unsavedView,
    view.name,
    view.settings,
    viewName,
    viewNames,
  ]);

  const handleViewUpdate = useCallback(async () => {
    const sortUpdated = !isEqualOmitUndefined(
      view.settings.sort,
      originalView?.settings.sort
    );
    const filterUpdated = !isEqualOmitUndefined(
      view.settings.filter,
      originalView?.settings.filter
    );
    const hideFieldsUpdated = !isEqualOmitUndefined(
      view.settings.fields,
      originalView?.settings.fields
    );

    const viewChangeList = getViewChangeList();
    const renamed = viewName !== originalView?.name;
    if (renamed) {
      const delta: ViewChangeType = {
        type: ViewChangeTypes.VIEW_NAME,
        value: viewName,
        delta: getTextDelta(view.name, viewName),
      };

      await dispatch(
        handleSaveView({
          contentId,
          view: { ...view, name: viewName },
          viewChangeList: [...viewChangeList, delta],
        })
      );
    } else {
      await dispatch(
        handleSaveView({
          contentId,
          view,
          viewChangeList,
        })
      );
    }

    setViewId(view.id);
    setUnsavedChanges('ViewSettingsPanel', false);
    hidePanel({ ignoreUnsavedChanges: true });
    client.sendViewUpdatedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'databaseViewSettingsPanel',
      viewId: view.id,
      selectedLayout: view.settings.layout.type,
      sortUpdated,
      filterUpdated,
      hideFieldsUpdated,
      renamed,
    });
    addFlag({
      id: 'view-updated',
      type: 'success',
      body: formatMessage(messages['view-settings-panel.update-view-flag-body'], {
        viewName: renamed ? viewName : view.name,
      }),
      timeout: 3000,
    });
  }, [
    view,
    originalView?.settings.sort,
    originalView?.settings.filter,
    originalView?.settings.fields,
    originalView?.name,
    getViewChangeList,
    viewName,
    setViewId,
    setUnsavedChanges,
    hidePanel,
    client,
    contentId,
    formatMessage,
    dispatch,
  ]);

  const onBack = useCallback(() => {
    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);
  }, [setActivePanelSetting]);

  const handleDeleteViewWithClose = useCallback(() => {
    handleDeleteView(view.id, 'databaseViewSettingsPanel', onClose);
  }, [handleDeleteView, onClose, view]);

  const isOverview = activePanelSetting === ViewPanelSettingType.OVERVIEW;

  const ViewSettingsBody = useCallback(() => {
    switch (activePanelSetting) {
      case ViewPanelSettingType.LAYOUT:
        return <ViewSettingsLayout contentId={contentId} FieldPicker={FieldPicker} />;
      case ViewPanelSettingType.FILTER:
        return (
          <ViewSettingsFilter
            contentId={contentId}
            FieldPicker={FieldPicker}
            FilterTypePicker={FilterTypePicker}
            FilterValueEdit={FilterValueEdit}
            autoFocusLastFilterValue={autoFocusLastFilterValue}
          />
        );
      case ViewPanelSettingType.SORT:
        return <ViewSettingsSort contentId={contentId} databaseFields={databaseFields} />;
      case ViewPanelSettingType.FIELDS:
        return (
          <ViewSettingsFields contentId={contentId} databaseFields={databaseFields} />
        );
    }
  }, [
    activePanelSetting,
    contentId,
    FieldPicker,
    FilterTypePicker,
    FilterValueEdit,
    autoFocusLastFilterValue,
    databaseFields,
  ]);

  useEffect(() => {
    setViewName(view.name);
  }, [view.name, setViewName]);

  return (
    <Stack xcss={stackStyles} spread="space-between" testId="view-settings-panel-content">
      <Box>
        <ViewSettingsHeader
          contentId={contentId}
          onDelete={handleDeleteViewWithClose}
          onClose={onClose}
          hasChanges={hasChanges}
          onBack={onBack}
          isOverview={isOverview}
          canEditViews={canEditViews}
          totalViews={totalViews}
        />
        <Box xcss={bodyBoxStyles}>
          {/* Overview cannot go in callback hook since it causes re-mounting on prop change
              which causes rename to re-mount */}
          {activePanelSetting === ViewPanelSettingType.OVERVIEW ? (
            <ViewSettingsOverview
              viewNameIsValid={viewNameIsValid}
              viewNames={viewNames}
              viewName={viewName}
              setViewName={setViewName}
              canEditViews={canEditViews}
            />
          ) : (
            <ViewSettingsBody />
          )}
        </Box>
      </Box>

      <ViewSettingsFooter
        contentId={contentId}
        handleViewUpdate={handleViewUpdate}
        handleSaveAsNewView={handleSaveAsNewView}
        viewNameIsValid={viewNameIsValid}
        hasChanges={hasChanges}
        canEditViews={canEditViews}
      />
    </Stack>
  );
};

export default ViewSettings;
